import React, { useState, useRef, useEffect, useMemo } from 'react';

const FiltroPredefinidosMultiselect = ({
  predefinidos,
  selectedIds,
  onChange,
  label = 'Tipo de movimiento',
  emptyLabel = 'Todos los tipos',
  oneSelectedFallback = '1 tipo',
  manySelectedLabel = (n) => `${n} tipos seleccionados`,
}) => {
  const ids = Array.isArray(selectedIds) ? selectedIds : [];
  const [abierto, setAbierto] = useState(false);
  const [localIds, setLocalIds] = useState([]);
  const ref = useRef(null);

  const ordenados = useMemo(
    () =>
      [...(predefinidos || [])].sort((a, b) =>
        (a.descripcion || '').localeCompare(b.descripcion || '', 'es', { sensitivity: 'base' })
      ),
    [predefinidos]
  );

  const mapaDescripcion = useMemo(() => {
    const m = new Map();
    ordenados.forEach((p) => m.set(p.id, p.descripcion || ''));
    return m;
  }, [ordenados]);

  useEffect(() => {
    if (abierto) setLocalIds([...ids]);
  }, [abierto, ids]);

  useEffect(() => {
    if (!abierto) return undefined;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onChange(localIds);
        setAbierto(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [abierto, localIds, onChange]);

  const toggle = (id) => {
    setLocalIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleCerrar = () => {
    onChange(localIds);
    setAbierto(false);
  };

  const handleLimpiar = () => {
    setLocalIds([]);
    onChange([]);
    setAbierto(false);
  };

  const textoBoton = (() => {
    if (ids.length === 0) return emptyLabel;
    if (ids.length === 1) return mapaDescripcion.get(ids[0]) || oneSelectedFallback;
    return typeof manySelectedLabel === 'function'
      ? manySelectedLabel(ids.length)
      : `${ids.length} seleccionados`;
  })();

  return (
    <div className="filtro-predefinidos" ref={ref}>
      <button
        type="button"
        className="filtro-predefinidos-trigger"
        onClick={() => {
          if (abierto) onChange(localIds);
          setAbierto((o) => !o);
        }}
        aria-expanded={abierto}
        aria-haspopup="listbox"
        aria-label={label}
        disabled={ordenados.length === 0}
        title={label}
      >
        <span className="filtro-predefinidos-trigger-text">
          {ordenados.length === 0 ? 'Sin tipos disponibles' : textoBoton}
        </span>
        <span className="filtro-predefinidos-chevron" aria-hidden="true">▾</span>
      </button>
      {abierto && ordenados.length > 0 && (
        <div className="filtro-predefinidos-dropdown" role="listbox" aria-label={label}>
          <div className="filtro-predefinidos-dropdown-header">
            <span>{label}</span>
            {localIds.length > 0 && (
              <button type="button" className="filtro-predefinidos-limpiar" onClick={handleLimpiar}>
                Limpiar
              </button>
            )}
          </div>
          <ul className="filtro-predefinidos-lista">
            {ordenados.map((p) => (
              <li key={p.id}>
                <label className="filtro-predefinidos-option">
                  <input
                    type="checkbox"
                    checked={localIds.includes(p.id)}
                    onChange={() => toggle(p.id)}
                  />
                  <span>{p.descripcion}</span>
                </label>
              </li>
            ))}
          </ul>
          <div className="filtro-predefinidos-footer">
            <button type="button" className="filtro-predefinidos-listo" onClick={handleCerrar}>
              Listo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FiltroPredefinidosMultiselect;
